/*
 * ReviewController — Delta-Review-Logik
 *
 * Empfaengt propose_*-Events vom AgentTransport, puffert sie bis zum Ende der
 * Anthropic-Runde (`done`-Event), wechselt dann in den Review-Modus: User
 * navigiert mit A/R/T/←/→ durch die Vorschlaege, der Controller applied bzw.
 * verwirft und meldet das Ergebnis per reportActionDone zurueck.
 *
 * Entkoppelt von der View: die eigentliche Canvas-Darstellung passiert ueber
 * zwei schmale Slots (`viewHint` fuer EditorView, `panel` fuer CoreConsole),
 * beide werden per Constructor-Injection gesetzt und sind fuer Tests
 * trivial mockbar.
 *
 * Spec: docs/archive/ai-agent-specification.md §7 (Smart Delta Review).
 *
 * Auto-Apply (Phase 3) wird ueber eine deklarative Tool-Map getrieben — siehe
 * js/agent/autoApplyToolMap.js. Pro Spec entsteht eine applyXxx-Methode auf
 * dem Prototyp (Generierungs-Schleife am Datei-Ende), Engine `_runAutoApply`
 * macht den Rest. Phase 4 ist dann ein Tabellen-Eintrag, kein neuer Handler.
 *
 * Zustaende:
 *   IDLE         — kein Review aktiv
 *   COLLECTING   — Propose-Events kommen rein, Puffer fuellt sich
 *   REVIEWING    — Puffer fertig, User navigiert
 *   DONE         — Review beendet (Cleanup laeuft, danach zurueck zu IDLE)
 *
 * Uebergaenge:
 *   IDLE → COLLECTING   : erster propose_*-Event
 *   COLLECTING → REVIEWING : done-Event oder (spaeter) Grace-Timer
 *   REVIEWING → DONE    : letzter Vorschlag entschieden oder Escape/Abort
 *
 * Main-Pane-Lock:
 *   isActive ist true in COLLECTING und REVIEWING. keyboardHandlers prueft
 *   das und interceptet A/R/T/←/→/Escape — nur wenn Main oder Core-Pane
 *   aktiv sind (Outline/Context/Background bleiben frei, siehe Spec §7).
 */

import { AUTO_APPLY_TOOLS } from '../agent/autoApplyToolMap.js'

export const ReviewState = Object.freeze({
    IDLE: 'IDLE',
    COLLECTING: 'COLLECTING',
    REVIEWING: 'REVIEWING',
    DONE: 'DONE'
})

export const ProposalStatus = Object.freeze({
    PENDING: 'pending',
    APPLIED: 'applied',
    REJECTED: 'rejected',
    VERSION_MISMATCH: 'version_mismatch',
    DROPPED: 'dropped'          // z.B. durch Remote-Change waehrend Review
})

/** Heartbeat-Intervall (siehe Spec §7). Im Mock No-Op. */
const HEARTBEAT_MS = 60 * 1000

export class ReviewController {
    /**
     * @param {object} deps
     * @param {EditorModel} deps.model - fuer Apply (replaceParagraphText, insertParagraphAfter, deleteParagraph)
     * @param {AgentTransport} [deps.transport] - fuer reportActionDone; optional fuer Tests
     * @param {object} [deps.viewHint] - { setReviewHint({dimAllExcept, overlays}), clearReviewHint() }
     * @param {object} [deps.panel] - { setReviewPanel(info), clearReviewPanel(), logSystem(text), logError(text) }
     * @param {function} [deps.onStateChange] - (state) => void, fuer Render-Trigger
     * @param {function} [deps.versionLookup] - (paragraphId) => number | undefined; fuer Auto-Apply Versions-Check
     */
    constructor({
        model, transport = null, viewHint = null, panel = null, onStateChange = null,
        flushBeforeAck = null, versionLookup = null
    } = {}) {
        this.model = model
        this.transport = transport
        this.viewHint = viewHint
        this.panel = panel
        this._onStateChange = onStateChange

        // Phase 3: Auto-Apply-Pfad. Optionale async-Funktion, die VOR
        // reportActionDone gerufen wird — z.B. SyncService.flushNow(projectId).
        // Sorgt dafuer, dass der Server-Loop bei Folge-Tool-Calls den frischen
        // DB-Stand sieht. Im Mock-Pfad nicht gesetzt (kein Sync noetig).
        this._flushBeforeAck = flushBeforeAck

        // Versions-Lookup fuer den Auto-Apply Versions-Check. Das EditorModel
        // pflegt KEIN paragraph.version-Feld — die Wahrheit lebt im
        // ProjectManager._paragraphMeta. Ohne Lookup ist der Check effektiv
        // ein No-Op (kein Mismatch), womit Apply blind durchgeht. Im Real-
        // Pfad (main.js) wird ein Lookup auf projectManager.getParagraphVersion
        // injected; Mock-Pfad und Tests lassen den Lookup weg.
        this._versionLookup = versionLookup

        this._state = ReviewState.IDLE
        this._suggestions = []        // {type, toolCallId, payload, status, activeView}
        this._index = 0
        this._heartbeatTimer = null

        // Sammelpuffer fuer Verdicts (HITL == end_turn, Spec §6). AgentService
        // ruft takeBatchedResults() vor jedem sendStreaming auf, damit die
        // Verdicts als reviewResults im naechsten /api/agent/message-Body
        // mitfliessen.
        this._pendingResults = []
    }

    // === Logging-Helper ===
    //
    // Tool-Apply-Status (skipped/failed/applied-Meldungen) gehoeren in den
    // aktiven THINKING-Block, damit der User sie nicht zwischen seiner
    // eigenen Antwort als SYSTEM-Block sieht. Wenn das Panel ein
    // logToolStatus/logToolError-API anbietet, nutzen wir das; sonst
    // Fallback auf logSystem/logError (Test-Mocks, alter Pfad).

    _logStatus(text) {
        if (this.panel?.logToolStatus) this.panel.logToolStatus(text)
        else this.panel?.logSystem?.(text)
    }

    _logErr(text) {
        if (this.panel?.logToolError) this.panel.logToolError(text)
        else this.panel?.logError?.(text)
    }

    // === Public Getter ===

    /** @returns {string} aktueller Zustand */
    get state() { return this._state }

    /** @returns {boolean} ob der Controller in einem UI-aktiven Zustand ist (Lock aktiv) */
    get isActive() {
        return this._state === ReviewState.COLLECTING || this._state === ReviewState.REVIEWING
    }

    /** @returns {boolean} ob der User gerade navigiert (A/R/T/←/→ wirken) */
    get isReviewing() {
        return this._state === ReviewState.REVIEWING
    }

    /** @returns {number} aktueller Vorschlag-Index (0-basiert) */
    get index() { return this._index }

    /** @returns {number} Anzahl Vorschlaege im Puffer */
    get count() { return this._suggestions.length }

    /** @returns {object|null} aktueller Vorschlag (oder null) */
    get currentSuggestion() {
        return this._suggestions[this._index] || null
    }

    /** @returns {object[]} flache Liste aller Vorschlaege (Read-only fuer View) */
    get suggestions() { return this._suggestions }

    // === Transport-Event-Receiver ===
    // Werden vom main.js als Handler an MockAgentTransport.sendMessage uebergeben

    onProposeRewrite(payload) {
        // No-Op-Erkennung liegt seit der Markdown-First-Migration server-
        // seitig im Tool (suggestRewrite.js#isNoOp) — der Server vergleicht
        // den vollen Patch (text + formats + links + Block) gegen den
        // existierenden Stand und schickt bei No-Op gar kein SSE-Event,
        // sondern liefert status 'no_change' direkt im Tool-Result. Dem
        // Client kann das nicht zuverlaessig auffallen, weil der Payload
        // nur den NEUEN Stand traegt und ein reiner Format-Change Text-
        // Identitaet hat. Hier also bedingungslos in den Review-Puffer.
        this._addSuggestion('rewrite', payload)
    }

    onProposeInsert(payload) {
        this._addSuggestion('insert', payload)
    }

    onProposeDelete(payload) {
        this._addSuggestion('delete', payload)
    }

    /** Called when the agent's turn ends. Starts REVIEWING if we have suggestions. */
    onDone() {
        if (this._state === ReviewState.COLLECTING) {
            this._flushToReviewing()
        }
        // Wenn wir noch IDLE sind: keine Vorschlaege, nichts zu tun.
    }

    /**
     * Phase 4.5: Server hat `await_review` aufgerufen — der Loop pausiert auf
     * dem Tool und der SSE-Stream bleibt offen. Wir flushen die gepufferten
     * Vorschlaege jetzt in REVIEWING, damit der User navigieren kann (A/R/T/
     * Pfeil/Tab). Ohne diesen Hook bliebe der Controller in COLLECTING, weil
     * der `done`-SSE erst nach `await_review_done` + `end_turn` kommt — die
     * Tasten greifen dann nicht und der Vorschlag bleibt unsichtbar.
     */
    onAwaitReviewStarted() {
        if (this._state === ReviewState.COLLECTING) {
            this._flushToReviewing()
        }
    }

    /** Called on abort — Review-Cleanup. Bereits akzeptierte Vorschlaege bleiben drin (Spec §7). */
    onAborted() {
        if (this._state === ReviewState.IDLE) return
        this._endReview({ aborted: true })
    }

    /**
     * Fuer zukuenftige Integration: Remote-Sync bringt eine Aenderung auf einem
     * Paragraph, der im aktuellen Review referenziert ist → Vorschlag verwerfen.
     * Mock-Scenarios triggern das direkt (siehe scenario remote-change-during-review).
     * @param {Set<string|number>|Array<string|number>} paragraphIds
     */
    onRemoteChange(paragraphIds) {
        if (!this.isActive) return
        const ids = paragraphIds instanceof Set ? paragraphIds : new Set(paragraphIds)
        let changed = false
        for (const s of this._suggestions) {
            if (s.status !== ProposalStatus.PENDING) continue
            const pid = s.type === 'insert' ? s.payload.afterParagraphId : s.payload.paragraphId
            if (pid != null && ids.has(pid)) {
                s.status = ProposalStatus.DROPPED
                changed = true
                this._reportResult(s.toolCallId, 'rejected')
                this._logStatus(`Paragraph ${pid} changed externally — proposal dropped.`)
            }
        }
        if (changed) this._afterSuggestionChange()
    }

    // === Phase-3 Auto-Apply Engine ===
    //
    // Statt 8 imperativer applyXxx-Methoden treibt eine generische Engine die
    // deklarative Tool-Map (js/agent/autoApplyToolMap.js). Pro Spec wird am
    // Datei-Ende eine applyXxx-Methode auf den Prototyp gehaengt — bestehende
    // API (rc.applyRewriteApply etc.) bleibt unveraendert.
    //
    // Pfad: Versions-Check → Adapter → Mutation am Modell (eigener Undo-Schritt)
    // → flushBeforeAck (Sync zum Server) → transport.reportActionDone(verdict).
    // Server resolved damit das pending awaitActionDone-Promise und der
    // ReAct-Loop laeuft mit frischem DB-Stand weiter.
    //
    // Kein ReviewController-State (COLLECTING/REVIEWING) — Auto-Apply ist
    // unabhaengig vom Review-Pfad. Der User sieht das Ergebnis sofort und kann
    // via Cmd+Z rueckgaengig machen.

    async _runAutoApply(spec, payload) {
        if (spec.versionField) {
            const targetId = payload[spec.versionField]
            if (this._versionMismatch(targetId, payload.baseVersion)) {
                if (spec.versionMismatchLog) this._logStatus(spec.versionMismatchLog)
                await this._autoApplyAck(payload.toolCallId, 'version_mismatch')
                return
            }
        }
        // Server-State-Tools (z.B. update_user_memory): modelMethod=null
        // signalisiert "kein Editor-Apply, einfach acken". Der Server hat
        // den State bereits geschrieben und nutzt den Round-Trip nur fuer
        // Console-Sichtbarkeit + einheitliche Tool-Architektur.
        if (spec.modelMethod === null) {
            const okExtras = spec.okExtras ? spec.okExtras(null, payload, this) : {}
            this._logStatus(spec.successLog(null, payload))
            await this._autoApplyAck(payload.toolCallId, 'applied', okExtras)
            return
        }
        const args = spec.payloadAdapter(payload)
        const fn = this.model[spec.modelMethod]
        if (typeof fn !== 'function') {
            this._logErr(`${spec.failLogPrefix}: model.${spec.modelMethod} missing`)
            await this._autoApplyAck(payload.toolCallId, 'rejected')
            return
        }
        const res = fn.apply(this.model, args)
        const isOk = spec.okPredicate
            ? spec.okPredicate(res)
            : (res ? res.ok !== false : false)
        if (!isOk) {
            const reason = res?.reason || 'rejected'
            this._logErr(`${spec.failLogPrefix}: ${reason}`)
            const extras = spec.errorExtras ? spec.errorExtras(res, payload) : {}
            await this._autoApplyAck(payload.toolCallId, reason, extras)
            return
        }
        const okExtras = spec.okExtras ? spec.okExtras(res, payload, this) : {}
        this._logStatus(spec.successLog(res, payload))
        await this._autoApplyAck(payload.toolCallId, 'applied', okExtras)
    }

    /**
     * Gemeinsamer Helper fuer alle Auto-Apply-Acks: optional flushBeforeAck,
     * dann transport.reportActionDone.
     *
     * Verdict-Mapping (Review v1.4 #11/#15): Server-Tools erwarten
     * 'applied'/'rejected'/'version_mismatch'. Wenn der Client einen Reason-Code
     * meldet (z.B. 'section_locked', 'section_not_empty', 'anchor_area_mismatch',
     * 'is_separator', 'unknown_section'), mappen wir auf 'rejected' und reichen
     * den Original-Code als `diagnostics` mit. Sonst landet das Verdict im
     * Server-Tool als `unexpected_verdict` und das Modell sieht einen unklaren
     * Fehler statt eines sauberen "User hat abgelehnt — Grund X".
     */
    async _autoApplyAck(toolCallId, result, extra = {}) {
        const VALID = new Set(['applied', 'rejected', 'version_mismatch'])
        let mappedResult = result
        let diagnostics
        if (!VALID.has(result)) {
            diagnostics = result
            mappedResult = 'rejected'
        }
        if (this._flushBeforeAck) {
            try { await this._flushBeforeAck() } catch (err) {
                console.warn('[ReviewController] flushBeforeAck threw:', err)
            }
        }
        try {
            const info = { toolCallId, result: mappedResult, ...extra }
            if (diagnostics) info.diagnostics = diagnostics
            this.transport?.reportActionDone?.(info)
        } catch (err) {
            console.warn('[ReviewController] reportActionDone threw:', err)
        }
    }

    /**
     * Versions-Check fuer Auto-Apply-Pfade (Review v1.4 #3). Wenn die lokale
     * Version eines Paragraphen/Separators von der baseVersion im Tool-Payload
     * abweicht, ist zwischen Server-Read und Client-Apply etwas geaendert worden
     * — Apply abbrechen, version_mismatch zurueck, Modell kann re-readen.
     *
     * @param {*} targetId  paragraphId oder separatorId
     * @param {number|undefined|null} baseVersion
     * @returns {boolean} true wenn Mismatch (Apply NICHT durchfuehren)
     */
    _versionMismatch(targetId, baseVersion) {
        if (baseVersion == null) return false
        // Lookup-Reihenfolge: injected versionLookup (ProjectManager-Cache),
        // dann paragraph.version am Modell (faktisch nie gesetzt). Wenn beide
        // keine Zahl liefern, fehlt die lokale Wahrheit — wir behandeln das
        // bewusst als "kein Mismatch": der Server hat die DB gerade gelesen,
        // ein lokaler Stand der davon abweichen koennte ist nicht bekannt.
        let localVersion
        if (this._versionLookup) {
            const v = this._versionLookup(targetId)
            if (typeof v === 'number') localVersion = v
        }
        if (localVersion == null) {
            const para = this.model.getParagraph?.(targetId)
            if (para && typeof para.version === 'number') localVersion = para.version
        }
        if (typeof localVersion !== 'number') return false
        return localVersion !== baseVersion
    }

    // === Keyboard ===

    /**
     * Interceptet A/R/T/X/←/→ waehrend REVIEWING (COLLECTING blockiert
     * auch — dann gibt es aber noch keinen sichtbaren Vorschlag).
     *
     * Hinweis: Escape ist bewusst NICHT belegt — es toggelt weiterhin das
     * Pane-Display. Abbrechen geht ueber X.
     * @param {KeyboardEvent} e
     * @returns {boolean} true wenn das Event konsumiert wurde
     */
    handleKey(e) {
        if (!this.isActive) return false

        // Escape nicht konsumieren — gibt an den Pane-Fullscreen-Toggle durch.
        if (e.key === 'Escape') return false

        // X: Review abbrechen, nicht-resolvte als rejected melden
        if (e.key === 'x' || e.key === 'X') {
            this._cancelRemaining()
            return true
        }

        // Im COLLECTING-Zustand nur X (siehe oben) — User kann sonst nichts tun,
        // aber die Sperre fuer andere Keys bleibt, sonst schreibt der Editor
        // in gerade vorgeschlagene Paragraphen.
        if (this._state !== ReviewState.REVIEWING) return true

        const key = e.key
        if (key === 'a' || key === 'A') { this._acceptCurrent(); return true }
        if (key === 'r' || key === 'R') { this._rejectCurrent(); return true }
        if (key === 't' || key === 'T') { this._toggleCurrentView(); return true }
        if (key === 'ArrowRight') { this._goNext(); return true }
        if (key === 'ArrowLeft') { this._goPrev(); return true }
        // Tab/Shift+Tab als zusaetzliche Navigation — natuerlich auf der
        // Tastatur, weil ←/→ je nach Layout schwer erreichbar sind.
        if (key === 'Tab') {
            if (e.shiftKey) this._goPrev(); else this._goNext()
            return true
        }

        // Alle anderen Keys schlucken wir ebenfalls — Spec §7: normale
        // Eingabe im Main-Pane ist waehrend Review gesperrt.
        return true
    }

    // === Internes ===

    _addSuggestion(type, payload) {
        // Bei Bedarf: ersten Propose startet COLLECTING
        if (this._state === ReviewState.IDLE) {
            this._state = ReviewState.COLLECTING
            this._suggestions = []
            this._notifyStateChange()
        }
        this._suggestions.push({
            type,
            toolCallId: payload.toolCallId,
            payload,
            status: ProposalStatus.PENDING,
            activeView: 'proposed'    // Default: Vorschlag im Vordergrund (T togglet zu 'original')
        })
    }

    _flushToReviewing() {
        // Keine Vorschlaege gesammelt? Dann zurueck nach IDLE ohne Review.
        if (this._suggestions.length === 0) {
            this._state = ReviewState.IDLE
            this._notifyStateChange()
            return
        }
        this._state = ReviewState.REVIEWING
        this._index = 0
        this._startHeartbeat()
        this._syncView()
        this._syncPanel()
        this._scrollToCurrent()
        this._notifyStateChange()
    }

    _acceptCurrent() {
        const s = this.currentSuggestion
        if (!s || s.status !== ProposalStatus.PENDING) return

        // Versions-Check — gleiche Semantik wie _versionMismatch (Auto-Apply):
        // ohne lokale Wahrheit (versionLookup undefined + paragraph.version
        // fehlt) gilt das nicht als Mismatch — Apply geht durch.
        if (s.type !== 'insert' && this._versionMismatch(s.payload.paragraphId, s.payload.baseVersion)) {
            s.status = ProposalStatus.VERSION_MISMATCH
            this._reportResult(s.toolCallId, 'version_mismatch')
            this._logStatus(`Paragraph was changed concurrently — proposal auto-rejected.`)
            this._afterSuggestionChange()
            return
        }

        let applied = false
        let appliedVersion
        if (s.type === 'rewrite') {
            const res = this.model.replaceParagraphText(s.payload.paragraphId, s.payload.newText, {
                formats: s.payload.newFormats || [],
                links: s.payload.newLinks || [],
                headingLevel: s.payload.newHeadingLevel || 0,
                listType: s.payload.newListType || null,
                listLevel: s.payload.newListLevel || 0,
                blockquote: !!s.payload.newBlockquote,
                codeBlock: !!s.payload.newCodeBlock
            })
            applied = res.ok
            if (res.ok) appliedVersion = this._readVersion(s.payload.paragraphId)
            else this._logErr(`Apply failed: ${res.reason}`)
        } else if (s.type === 'insert') {
            const originalAnchor = s.payload.afterParagraphId ?? null
            const res = this.model.insertParagraphAfter(originalAnchor, s.payload.newText, {
                formats: s.payload.newFormats || [],
                links: s.payload.newLinks || [],
                headingLevel: s.payload.newHeadingLevel || 0,
                listType: s.payload.newListType || null,
                listLevel: s.payload.newListLevel || 0,
                blockquote: !!s.payload.newBlockquote,
                codeBlock: !!s.payload.newCodeBlock
            })
            applied = res.ok
            if (res.ok) {
                appliedVersion = this._readVersion(res.newParagraphId)
                // Re-Anchor nachfolgender pending Inserts mit demselben Anker
                // auf die frisch erzeugte newParagraphId. Damit:
                //  (a) reiht der naechste Apply den neuen Para HINTER den
                //      gerade applied an (sonst dreht sich die Reihenfolge um),
                //  (b) wandert die Vorschau im Review HINTER den applied Para,
                //      sodass dieser an seiner Vorschau-Position stehen bleibt
                //      und nicht ans Ende der Pending-Gruppe rutscht.
                if (originalAnchor != null && res.newParagraphId != null) {
                    for (let j = this._index + 1; j < this._suggestions.length; j++) {
                        const next = this._suggestions[j]
                        if (next.status !== ProposalStatus.PENDING) continue
                        if (next.type !== 'insert') continue
                        if ((next.payload.afterParagraphId ?? null) === originalAnchor) {
                            next.payload.afterParagraphId = res.newParagraphId
                        }
                    }
                }
            } else {
                this._logErr(`Apply failed: ${res.reason}`)
            }
        } else if (s.type === 'delete') {
            const paraId = s.payload.paragraphId
            if (this.model.getParagraph?.(paraId)) {
                // reviewApply: beim UNDO Cursor an die Aenderungs-Stelle.
                // Phase 3: deleteParagraph liefert jetzt {ok, reason} — wir
                // werten das aus, damit Lock-Konflikte nicht faelschlich als
                // 'applied' rausgehen.
                const res = this.model.deleteParagraph(paraId, { reviewApply: true })
                applied = res ? res.ok !== false : true
                if (!applied) this._logErr(`Apply failed: ${res?.reason}`)
            } else {
                this._logErr('Paragraph no longer exists.')
            }
        }

        s.status = applied ? ProposalStatus.APPLIED : ProposalStatus.REJECTED
        this._reportResult(s.toolCallId, s.status, appliedVersion)
        this._afterSuggestionChange()
    }

    _rejectCurrent() {
        const s = this.currentSuggestion
        if (!s || s.status !== ProposalStatus.PENDING) return
        s.status = ProposalStatus.REJECTED
        this._reportResult(s.toolCallId, 'rejected')
        this._afterSuggestionChange()
    }

    _toggleCurrentView() {
        const s = this.currentSuggestion
        if (!s || s.status !== ProposalStatus.PENDING) return
        s.activeView = s.activeView === 'proposed' ? 'original' : 'proposed'
        this._syncView()
        this._syncPanel()
    }

    _goNext() {
        const next = this._findNextPending(this._index + 1, +1)
        if (next >= 0) this._setIndex(next)
    }

    _goPrev() {
        const prev = this._findNextPending(this._index - 1, -1)
        if (prev >= 0) this._setIndex(prev)
    }

    _setIndex(idx) {
        this._index = idx
        this._syncView()
        this._syncPanel()
        this._scrollToCurrent()
    }

    /**
     * Scrollt den Main-Pane zum aktuell aktiven Vorschlag. Wichtig fuer
     * Multi-Vorschlag-Reviews: ohne Auto-Scroll sieht der User Vorschlaege
     * ausserhalb des Viewports nicht und kann mit der Locked-Tastatur auch
     * nicht hin scrollen. Insert-Vorschlaege ohne Anker (Dokumentanfang)
     * scrollen auf Zeile 0.
     */
    _scrollToCurrent() {
        const s = this.currentSuggestion
        if (!s) return
        // Bevorzugt die suggestion-aware API: zentriert die virtuelle Insert-
        // Vorschau-Line, nicht nur den Anker. Bei Multi-Insert-Reviews zeigen
        // alle Vorschlaege auf denselben Anker — ohne diese API blieb der
        // Viewport beim ersten Vorschlag kleben.
        try {
            if (typeof this.viewHint?.scrollToReviewSuggestion === 'function') {
                this.viewHint.scrollToReviewSuggestion(s)
                return
            }
            if (typeof this.viewHint?.scrollToParagraph === 'function') {
                const paraId = s.type === 'insert'
                    ? (s.payload.afterParagraphId ?? null)
                    : s.payload.paragraphId
                this.viewHint.scrollToParagraph(paraId)
            }
        } catch (err) {
            console.warn('[ReviewController] scroll-to-current threw:', err)
        }
    }

    _findNextPending(start, step) {
        for (let i = start; i >= 0 && i < this._suggestions.length; i += step) {
            if (this._suggestions[i].status === ProposalStatus.PENDING) return i
        }
        return -1
    }

    /**
     * Nach Accept/Reject/Drop: naechsten pending-Vorschlag anspringen. Wenn
     * keiner uebrig, Review beenden.
     */
    _afterSuggestionChange() {
        const next = this._findNextPending(this._index, +1) >= 0
            ? this._findNextPending(this._index, +1)
            : this._findNextPending(0, +1)
        if (next < 0) {
            this._endReview({ aborted: false })
            return
        }
        this._setIndex(next)
    }

    _cancelRemaining() {
        for (const s of this._suggestions) {
            if (s.status === ProposalStatus.PENDING) {
                s.status = ProposalStatus.REJECTED
                this._reportResult(s.toolCallId, 'rejected')
            }
        }
        this._endReview({ aborted: true })
    }

    _endReview({ aborted }) {
        const stats = {
            total: this._suggestions.length,
            applied: this._suggestions.filter(s => s.status === ProposalStatus.APPLIED).length,
            rejected: this._suggestions.filter(s => s.status === ProposalStatus.REJECTED).length,
            dropped: this._suggestions.filter(s => s.status === ProposalStatus.DROPPED).length,
            versionMismatch: this._suggestions.filter(s => s.status === ProposalStatus.VERSION_MISMATCH).length
        }
        this._state = ReviewState.DONE
        this._stopHeartbeat()
        this.viewHint?.clearReviewHint?.()
        this.panel?.clearReviewPanel?.()
        const summary = aborted
            ? `Review cancelled — ${stats.applied} accepted, ${stats.rejected} rejected${stats.versionMismatch ? `, ${stats.versionMismatch} skipped (changed concurrently)` : ''}.`
            : `${stats.total} reviewed — ${stats.applied} accepted, ${stats.rejected} rejected${stats.versionMismatch ? `, ${stats.versionMismatch} skipped (changed concurrently)` : ''}.`
        this.panel?.logSystem?.(summary)
        this._notifyStateChange()

        // Phase 4.5: wenn der Server in einem `await_review` blockiert, schicken
        // wir die gepufferten Verdicts via /review-done direkt — der Loop
        // resumed in derselben SSE-Verbindung. Sonst bleibt der Puffer fuer
        // den naechsten sendMessage-Aufruf liegen (klassischer Pfad).
        if (this.transport?.hasActiveAwaitReview?.()) {
            const verdicts = this.takeBatchedResults()
            if (verdicts.length > 0 && typeof this.transport.reportReviewDone === 'function') {
                Promise.resolve(this.transport.reportReviewDone(verdicts))
                    .catch(err => console.warn('[ReviewController] reportReviewDone threw:', err?.message))
            }
        }

        // Cleanup — zurueck nach IDLE, Puffer leeren
        this._suggestions = []
        this._index = 0
        this._state = ReviewState.IDLE
        this._notifyStateChange()
    }

    _reportResult(toolCallId, result, appliedVersion) {
        const info = { toolCallId, result }
        if (appliedVersion != null) info.appliedVersion = appliedVersion

        // Mock-Pfad: pro Vorschlag direkt an den Transport melden, damit
        // reaktive Scenarios via wait() darauf reagieren koennen. Marker
        // `reportsReviewVerdictsImmediately` gesetzt vom MockAgentTransport.
        //
        // Real-Pfad (Review v1.4 #2): Verdicts laufen ausschliesslich ueber
        // `_pendingResults` und werden im naechsten /api/agent/message-Body
        // als `reviewResults` mitgeschickt. Frueher wurde `reportActionDone`
        // hier auch im Real-Pfad gefeuert — der Server hat den Call still
        // verworfen (kein passendes pendingActions-Eintrag), aber jeder
        // Verdict kostete einen extra HTTP-Roundtrip.
        if (this.transport?.reportsReviewVerdictsImmediately === true
            && this.transport?.reportActionDone) {
            try { this.transport.reportActionDone(info) } catch { /* ignorieren */ }
        }

        // Real-Pfad: Verdicts puffern, AgentService nimmt sie via takeBatchedResults().
        this._pendingResults.push(info)
    }

    /**
     * Liefert die seit dem letzten Aufruf gesammelten Verdicts UND leert den
     * Puffer. AgentService ruft das vor jedem sendStreaming auf.
     * @returns {Array<{toolCallId, result, appliedVersion?}>}
     */
    takeBatchedResults() {
        const out = this._pendingResults
        this._pendingResults = []
        return out
    }

    _readVersion(paragraphId) {
        const para = this.model.getParagraph?.(paragraphId)
        if (para && typeof para.version === 'number') return para.version
        return undefined
    }

    _syncView() {
        if (!this.viewHint?.setReviewHint) return
        const dimAllExcept = new Set()
        const overlays = []
        for (let i = 0; i < this._suggestions.length; i++) {
            const s = this._suggestions[i]
            if (s.status !== ProposalStatus.PENDING) continue
            const isActive = i === this._index
            if (s.type === 'rewrite') {
                dimAllExcept.add(s.payload.paragraphId)
                overlays.push({
                    kind: 'rewrite',
                    toolCallId: s.toolCallId,
                    anchorParagraphId: s.payload.paragraphId,
                    originalText: s.payload.originalText,
                    proposedText: s.payload.newText,
                    // Inline + Block-Felder fuer den Format-faehigen Composite-Renderer.
                    // EditorView.setReviewHint liest diese Felder; ohne sie faellt
                    // der Renderer auf Plaintext-Diff zurueck (wie vor 2026-05-02).
                    proposedFormats: s.payload.newFormats || [],
                    proposedLinks: s.payload.newLinks || [],
                    proposedHeadingLevel: s.payload.newHeadingLevel || 0,
                    proposedListType: s.payload.newListType || null,
                    proposedListLevel: s.payload.newListLevel || 0,
                    proposedBlockquote: !!s.payload.newBlockquote,
                    activeView: s.activeView,
                    isActive
                })
            } else if (s.type === 'insert') {
                // Anker NICHT in dimAllExcept aufnehmen: Der Anker-Paragraph
                // bleibt im Insert-Vorschlag unveraendert, ist also nicht
                // Bestandteil des Reviews. Konsistente Sicht — der User soll
                // optisch sofort erkennen, was sich aendert (= die virtuelle
                // Insert-Line), und alle anderen Paragraphen sind gedimmt.
                // Die virtuelle Insert-Line hebt sich durch _reviewInsertOverlay
                // separat ab (siehe EditorView._renderLine: kein Dim-Wrapper).
                overlays.push({
                    kind: 'insert',
                    toolCallId: s.toolCallId,
                    anchorParagraphId: s.payload.afterParagraphId ?? null,
                    newText: s.payload.newText,
                    proposedFormats: s.payload.newFormats || [],
                    proposedLinks: s.payload.newLinks || [],
                    proposedHeadingLevel: s.payload.newHeadingLevel || 0,
                    proposedListType: s.payload.newListType || null,
                    proposedListLevel: s.payload.newListLevel || 0,
                    proposedBlockquote: !!s.payload.newBlockquote,
                    activeView: s.activeView,
                    isActive
                })
            } else if (s.type === 'delete') {
                dimAllExcept.add(s.payload.paragraphId)
                overlays.push({
                    kind: 'delete',
                    toolCallId: s.toolCallId,
                    anchorParagraphId: s.payload.paragraphId,
                    activeView: s.activeView,
                    isActive
                })
            }
        }
        this.viewHint.setReviewHint({ dimAllExcept, overlays })
    }

    _syncPanel() {
        if (!this.panel?.setReviewPanel) return
        const s = this.currentSuggestion
        this.panel.setReviewPanel({
            index: this._index,
            total: this._suggestions.length,
            resolvedCount: this._suggestions.filter(x => x.status !== ProposalStatus.PENDING).length,
            type: s?.type || null,
            reason: s?.payload?.reason || '',
            activeView: s?.activeView || 'proposed',
            blockDelta: this._computeBlockDeltaForPanel(s)
        })
    }

    /**
     * Berechnet ein Vorher/Nachher-Block-Delta fuer das Status-Panel. Liefert
     * null, wenn der Vorschlag keine Block-Felder aendert oder es kein
     * Rewrite/Insert ist (Delete hat keine Block-Aenderung).
     *
     * Das Panel zeigt das Delta als kleine Tabelle, sodass der User auch ohne
     * den T-Toggle-Test sieht, was sich an Heading/Liste/Blockquote aendert.
     */
    _computeBlockDeltaForPanel(s) {
        if (!s) return null
        if (s.type !== 'rewrite' && s.type !== 'insert') return null
        // Rewrite: Original-Stand am existierenden Paragraph; Insert: Original
        // ist der "leere" Stand (Plain, kein Heading).
        const para = s.type === 'rewrite' ? this.model.getParagraph?.(s.payload.paragraphId) : null
        const oldH = para?.headingLevel || 0
        const newH = s.payload.newHeadingLevel || 0
        const oldL = para?.listType || null
        const newL = s.payload.newListType || null
        const oldLv = para?.listLevel || 0
        const newLv = s.payload.newListLevel || 0
        const oldQ = !!para?.blockquote
        const newQ = !!s.payload.newBlockquote
        if (oldH === newH && oldL === newL && oldLv === newLv && oldQ === newQ) return null
        return {
            old: { headingLevel: oldH, listType: oldL, listLevel: oldLv, blockquote: oldQ },
            new: { headingLevel: newH, listType: newL, listLevel: newLv, blockquote: newQ }
        }
    }

    _notifyStateChange() {
        this._onStateChange?.(this._state)
    }

    _startHeartbeat() {
        this._stopHeartbeat()
        if (typeof this.transport?.reviewHeartbeat !== 'function') return
        this._heartbeatTimer = setInterval(() => {
            try { this.transport.reviewHeartbeat() } catch { /* ignorieren */ }
        }, HEARTBEAT_MS)
    }

    _stopHeartbeat() {
        if (this._heartbeatTimer) {
            clearInterval(this._heartbeatTimer)
            this._heartbeatTimer = null
        }
    }

    /** Fuer Tests / Cleanup. */
    destroy() {
        this._stopHeartbeat()
        this.viewHint?.clearReviewHint?.()
        this.panel?.clearReviewPanel?.()
    }
}

// Generiere applyXxx-Methoden aus der Tool-Map. Bestehende API
// (rc.applyRewriteApply, rc.applyDeleteApply, ...) bleibt unveraendert —
// neue Tools (z.B. Phase 4 update_context_info) sind ein Eintrag in
// js/agent/autoApplyToolMap.js, kein neuer Handler hier.
for (const spec of AUTO_APPLY_TOOLS) {
    ReviewController.prototype[`apply${spec.handlerName}`] =
        function (payload) { return this._runAutoApply(spec, payload) }
}
